import {
    Box,
    Heading,
    Stack,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import ActivityBadges from "components/selection-components/select-activity/activity-badges/activity-badges";
import WebRuleRenderer from "components/selection-components/select-activity/web-rule-renderer/web-rule-renderer";
import { useBookedActivities } from "components/selection-components/select-activity/use-reserved-activities";
import { useTimesToNudge } from "components/selection-components/select-activity/use-times-to-nudge";
import ShowMoreText from "components/show-more-text/show-more-text";
import { useCategorizedWebRules } from "hooks/use-categorized-web-rules";
import { PageType } from "logger/page-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { useMemo } from "react";
import { AvailableTimesSection } from "types/available-times";
import { TimeSlot } from "types/time-slot";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useResolvedGuests } from "hooks/use-resolved-guests";
import { TimeSetSkeleton } from "components/selection-components/select-time/time-set/time-set-skeleton";
import { WebRuleType } from "types/web-rule-type";
import { getAvailabilityStateForBadge } from "utils/availability-utils";
import { ExperienceSuggestions } from "components/booking-modal/experience-suggestions";
import { ExperienceSuggestion } from "utils/experience-suggestions";

interface IBookingModalBody {
    section?: AvailableTimesSection;
    /** All activity sections for badge aggregation. For experiences, pass all child sections.
     * Falls back to [section] when omitted. */
    badgeSections?: AvailableTimesSection[];
    maximumGapTime?: number;
    name: string;
    description?: string;
    showStartTimeHeading?: boolean;
    hideWebRuleHeader?: boolean;
    hasMenuChoice?: boolean;
    showAvailabilityBadge?: boolean;
    suggestions?: ExperienceSuggestion[];
    onSelectSuggestion?: (experienceId: number) => void;
    selectedTime: TimeSlot | undefined;
    setSelectedTime: (timeSlot: TimeSlot) => void;
    setSelectedWaitListTimes: (time: TimeSlot) => void;
    selectedWaitListTimes: TimeSlot[];
    ignoredRuleIds?: Set<number>;
    /** Start times (HH:mm) whose experience chain would conflict with other bookings. */
    chainConflictStartTimes?: Set<string>;
}

const BookingModalBody = ({
    section,
    badgeSections,
    maximumGapTime,
    name,
    description,
    showStartTimeHeading,
    hideWebRuleHeader,
    hasMenuChoice,
    showAvailabilityBadge,
    suggestions,
    onSelectSuggestion,
    selectedTime,
    setSelectedTime,
    setSelectedWaitListTimes,
    selectedWaitListTimes,
    ignoredRuleIds,
    chainConflictStartTimes,
}: IBookingModalBody) => {
    const state = useBookingStateContext();
    const { date, isBookingRequest } = state;
    const { isLoading: isLoadingAvailableTimes } = useAvailableTimes(date);
    const resolvedGuests = useResolvedGuests();
    const availabilityState = showAvailabilityBadge
        ? getAvailabilityStateForBadge(section?.timeSets ?? [], resolvedGuests, !!isBookingRequest)
        : null;
    const bookedActivities = useBookedActivities(section?.id ?? 0);
    const computedTimesToNudgeMap = useTimesToNudge(
        [section?.id ?? 0],
        ignoredRuleIds,
        chainConflictStartTimes
    );
    const timesToNudgeMap = computedTimesToNudgeMap;

    // Use the merged sequence so activity cards show bookable and waitlist times together.
    const { activityOrderedSequence, interactive, commentOnly, waitList } = useCategorizedWebRules(
        section?.timeSets ?? []
    );

    // Track activity time selection (advanced flow)
    usePageTracking({
        pageName: "SelectActivityTime",
        pageType: PageType.Modal,
        metadata: {
            activityName: name,
            activityId: section?.id.toString() ?? "",
            hasTimeslots: interactive.length > 0,
            hasWaitlist: waitList.some((webRule) => webRule.times.length > 0),
            timeSlotCount: interactive.reduce((acc, rule) => acc + rule.times.length, 0),
        },
    });

    const modalData = useMemo(() => {
        const hasTimeSlots = activityOrderedSequence.some(
            (item) => item.type === WebRuleType.Interactive
        );
        const hasWaitListTimeSlots = waitList.some((webRule) => webRule.times.length > 0);

        return {
            activity: {
                name,
                description,
                section: section,
            },
            ui: {
                hasTimeSlots,
                hasWaitListTimeSlots,
                hasCommentOnlySections: commentOnly.length > 0,
                hasMixedTypes: commentOnly.length > 0 && interactive.length > 0,
                orderedSequence: activityOrderedSequence,
            },
            context: {
                bookedActivities,
                timesToNudgeMap,
            },
        };
    }, [
        section,
        name,
        description,
        bookedActivities,
        activityOrderedSequence,
        interactive,
        commentOnly,
        waitList,
        timesToNudgeMap,
    ]);

    // The modal freezes its last item, so the previous date's times stay mounted until the new ones
    // arrive. Kept faded and inert rather than shown beside a skeleton, which moved content twice.
    const hasTimesToShow = modalData.ui.hasTimeSlots || modalData.ui.hasCommentOnlySections;
    const isRefreshingTimes = isLoadingAvailableTimes && hasTimesToShow;

    return (
        <Stack spacing={ThemeSpaceVariable.None}>
            <Stack
                spacing={ThemeSpaceVariable.XSmall}
                p={ThemeSpaceVariable.Medium}
                pb={ThemeSpaceVariable.Small}
            >
                <Heading
                    as="h2"
                    fontWeight="medium"
                    fontSize={ThemeFontSizeVariable.XLarge}
                    fontFamily={ThemeFontVariable.SubHeader}
                >
                    {modalData.activity.name}
                </Heading>
                {modalData.activity.section && (
                    <ActivityBadges
                        sections={badgeSections ?? [modalData.activity.section]}
                        maximumGapTime={maximumGapTime}
                        inModal
                        hasMenuChoice={hasMenuChoice}
                        availabilityState={availabilityState}
                    />
                )}
                <ShowMoreText
                    text={modalData.activity.description ?? ""}
                    initialLines={
                        modalData.ui.hasTimeSlots ||
                        modalData.ui.hasWaitListTimeSlots ||
                        isLoadingAvailableTimes
                            ? 2
                            : undefined
                    }
                    textProps={{ fontWeight: "400" }}
                />
            </Stack>

            {isLoadingAvailableTimes && !hasTimesToShow && (
                <Box p={ThemeSpaceVariable.Medium}>
                    <TimeSetSkeleton px={ThemeSpaceVariable.None} />
                </Box>
            )}

            <Stack p={ThemeSpaceVariable.Medium} spacing={ThemeSpaceVariable.None}>
                {hasTimesToShow && (
                    <Box
                        opacity={isRefreshingTimes ? 0.4 : 1}
                        pointerEvents={isRefreshingTimes ? "none" : undefined}
                        aria-busy={isRefreshingTimes}
                    >
                        <WebRuleRenderer
                            orderedSequence={modalData.ui.orderedSequence}
                            selectedTime={selectedTime}
                            onTimeClick={setSelectedTime}
                            selectedWaitListTimes={selectedWaitListTimes}
                            onWaitListTimeClick={setSelectedWaitListTimes}
                            timesToNudgeMap={modalData.context.timesToNudgeMap}
                            showStartTimeHeading={showStartTimeHeading}
                            hideWebRuleHeader={hideWebRuleHeader}
                            ignoredRuleIds={ignoredRuleIds}
                            chainConflictStartTimes={chainConflictStartTimes}
                        />
                    </Box>
                )}
                <ExperienceSuggestions
                    suggestions={suggestions ?? []}
                    onSelect={onSelectSuggestion}
                />
            </Stack>
        </Stack>
    );
};

export default BookingModalBody;
